/**
 * <bag-drawer>: the bag drawer's behaviour. Its markup and lines come from
 * sections/bag-drawer.liquid and are re-rendered by cart.js, so every listener
 * is delegated. Opens on any [data-bag-open] click or `element.open()`; closes
 * on the scrim, CLOSE or Escape.
 */

import { changeLine, removeBuild } from "@theme/cart";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

class BagDrawer extends HTMLElement {
  #opener = null;

  connectedCallback() {
    this.addEventListener("click", this.#onClick);
    document.addEventListener("click", this.#onDocumentClick);
    document.addEventListener("keydown", this.#onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.#onDocumentClick);
    document.removeEventListener("keydown", this.#onKeydown);
    this.close();
  }

  get isOpen() {
    return !this.hidden;
  }

  open(opener = document.activeElement) {
    if (this.isOpen) return;
    this.#opener = opener instanceof HTMLElement ? opener : null;
    this.hidden = false;
    document.documentElement.style.overflow = "hidden";
    this.#setExpanded(true);
    this.#focusClose();
  }

  close() {
    if (!this.isOpen) return;
    this.hidden = true;
    document.documentElement.style.overflow = "";
    this.#setExpanded(false);
    if (this.#opener?.isConnected) this.#opener.focus();
    this.#opener = null;
  }

  #focusClose() {
    this.querySelector("button[data-bag-close]")?.focus();
  }

  #setExpanded(expanded) {
    for (const opener of document.querySelectorAll("[data-bag-open]")) {
      opener.setAttribute("aria-expanded", String(expanded));
    }
  }

  #onDocumentClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const opener = event.target.closest("[data-bag-open]");
    if (!opener) return;
    event.preventDefault();
    this.open(opener);
  };

  #onClick = (event) => {
    if (event.target.closest("[data-bag-close]")) {
      this.close();
      return;
    }
    const buildButton = event.target.closest("[data-remove-build]");
    if (buildButton) {
      this.#remove(buildButton, () => removeBuild(buildButton.dataset.removeBuild));
      return;
    }
    const lineButton = event.target.closest("[data-remove-line]");
    if (lineButton) {
      this.#remove(lineButton, () => changeLine(lineButton.dataset.removeLine, 0));
    }
  };

  async #remove(button, action) {
    button.disabled = true;
    try {
      await action();
    } catch {
      button.disabled = false;
      return;
    }
    this.#focusClose();
  }

  #onKeydown = (event) => {
    if (!this.isOpen) return;
    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== "Tab") return;

    const focusable = [...this.querySelectorAll(FOCUSABLE)];
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!this.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
}

if (!customElements.get("bag-drawer")) {
  customElements.define("bag-drawer", BagDrawer);
}
